import { Injectable } from "@angular/core";
import { CountryCode } from "libphonenumber-js";
import { BehaviorSubject, ReplaySubject } from "rxjs";
import { UserContextProvider } from "../../../../infrastructure/context/user-context-provider";
import { US_COUNTRY_CODE } from "../../../../infrastructure/context/UserContext";
import { NavService } from "../../../../layout/nav/nav.service";
import { Country } from "../../../../shared/address/Country";
import { State } from "../../../../shared/address/State";
import { CountryService } from "../../../../shared/reference/country.service";
import { ReferenceService } from "../../../../shared/reference/reference.service";
import { GuestService } from "../../services/guest.service";
import { GuestScreeningRequest, ScreeningRequestGuestInformation } from "../models/GuestScreeningRequest.model";
import { Logger, LogLevel } from "../../../../infrastructure/logging/logger";

export interface GuestScreeningError {
    message: string;
    error: String;
}

@Injectable()
export class GuestScreeningService {

    private states: BehaviorSubject<State[]> = new BehaviorSubject<State[]>([]);
    private countries: BehaviorSubject<Country[]> = new BehaviorSubject<Country[]>([]);
    private ageRestrictions: { min: Date, max: Date } = { min: new Date(), max: new Date() };
    private countryCode: BehaviorSubject<CountryCode | null> = new BehaviorSubject<CountryCode | null>(null);
    private phoneNumberFormat: BehaviorSubject<string> = new BehaviorSubject<string>('');
    private formState: any = {};

    private loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private error: ReplaySubject<GuestScreeningError> = new ReplaySubject<GuestScreeningError>();
    private submitted: ReplaySubject<string> = new ReplaySubject<string>(1);

    organizationId: any;

    constructor(
        private logger: Logger,
        private countryService: CountryService,
        private referenceService: ReferenceService,
        private userContextProvider: UserContextProvider,
        private guestApi: GuestService,
        private navService: NavService
    ) {}

    public load() {
        try {
            this.loading.next(true);

            if (this.navService.selectedOrgId.value > 0) {
                this.organizationId = this.navService.selectedOrgId.value;
            }

            this.setupAgeRestrictions();
            this.loadStatesAndCountries();
            this.preselectUnitedStatesIfNaUser();
            this.loading.next(false);
        } catch (e) {
            let error;
            if(e instanceof Error) {
                error = e.message;
            } else {
                error = JSON.stringify(e);
            }
            this.addErorr({ message: 'Failed to load guest screening form', error });
        }
    }

    private loadStatesAndCountries() {
        this.states.next(this.referenceService.getAllStates());

        this.countryService.getCountries()
            .then(countries => {
                if(this.userContextProvider.isNaUser()) {
                    countries = countries.filter(country => [1, 180, 244].includes(country.id));
                }
                
                this.countries.next(countries)
            })
            .catch(error => this.error.next({ message: 'Failed to load available countries', error: error }));
    }

    private setupAgeRestrictions() {
        const today = new Date();
        const minDate = new Date(1900, 0, 1);
        const maxDate = new Date(
          today.getFullYear() - 18,
          today.getMonth(),
          today.getDate()
        );

        this.ageRestrictions = { min: minDate, max: maxDate };
    }

    private preselectUnitedStatesIfNaUser() {
        if(this.userContextProvider.isNaUser()) {
            this.countryCode$.next(US_COUNTRY_CODE as CountryCode);
        }
    }

    public async submit() {
        try {
            this.loading.next(true);

            const country = this.getCountryFromId(this.formState.country);
            if(!country) {
                this.error.next({ message: 'Invalid country, cannot submit Guest', error: `Country with id ${this.formState.country} not found` });
                this.loading.next(false);
                return;
            }
            const request: GuestScreeningRequest = this.buildRequest(country);
            const response = await this.guestApi.createGuestVerification(request);
            if(!response) {
                this.error.next({ message: 'Failed to submit Guest', error: 'Create guest verification response null' });
                this.loading.next(false);
                return;
            }

            const guestId = response[0]?.guest_id;
            if(!guestId) {
                this.error.next({ message: 'Failed to submit Guest', error: 'GuestId not found in response' });
                this.loading.next(false);
                return;
            }

            this.submitted.next(guestId);

            this.loading.next(false);
        } catch (e) {
            let error;
            if(e instanceof Error) {
                error = e.message;
            } else {
                error = JSON.stringify(e);
            }
            this.addErorr({ message: 'An error occurred while submitting Guest', error });
        }
    }

    private buildRequest(country: Country): GuestScreeningRequest {
        const guest: ScreeningRequestGuestInformation = {
            organization_id: this.organizationId,
            first_name: this.formState.firstName,
            middle_name: this.formState.middleName,
            last_name: this.formState.lastName,
            birth_date: this.formState.birthDate,
            email: this.formState.email,
            phone_number: this.formState.phoneNumber,
            address: {
                country: {
                    id: country.id,
                    display_text: country.display_text
                },
                country_id: country.id,
                country_name: country.display_text,
                address_line_1: this.formState.addressLine1,
                address_line_2: this.formState.addressLine2,
                city: this.formState.city,
                postal_code: this.formState.postalCode,
                state: this.formState.state
            }
        }

        return {
            organization_id: this.organizationId,
            guests: [guest]
        }
    }

    public updateFormState(state: any) {
        this.formState = state;
    }

    public updatePhoneNumberFormat(format: string) {
        if(format !== this.phoneNumberFormat.value) {
            this.phoneNumberFormat.next(format);
        }
    }

    public updateCountryCodeFromSelectedCountry(countryId: string) {
        if(!countryId) {
            return;
        }
        const country = this.getCountryFromId(parseInt(countryId));
        this.countryCode$.next(country?.code as CountryCode);
    }

    private getCountryFromId(countryId: number): Country | undefined {
        return this.countries$.value.find(c => c.id === countryId);
    }

    public get loading$(): BehaviorSubject<boolean> {
        return this.loading;
    }

    public get minAgeRestriction(): Date {
        return this.ageRestrictions.min;
    }

    public get maxAgeRestriction(): Date {
        return this.ageRestrictions.max;
    }

    public get states$(): BehaviorSubject<State[]> {
        return this.states;
    }

    public get countries$(): BehaviorSubject<Country[]> {
        return this.countries;
    }

    public get countryCode$(): BehaviorSubject<CountryCode | null> {
        return this.countryCode;
    }

    public get error$(): ReplaySubject<GuestScreeningError> {
        return this.error;
    }

    public get submitted$(): ReplaySubject<string> {
        return this.submitted;
    }
    
    public get phoneNumberFormat$(): BehaviorSubject<string> {
        return this.phoneNumberFormat;
    }

    private addErorr(error: GuestScreeningError) {
        this.error.next(error);
        this.logger.log({ message: error.message, level: LogLevel.ERROR });
    }
}